import { useRef, useEffect } from "react"
import { X, LoaderCircle, Sparkles } from "lucide-react"
import { ToMarkdown } from "@/components/ui/tomarkdown"

export interface ChatMessage {
  role: "user" | "assistant"
  content: string
}

interface ChatPanelProps {
  agentId: string
  agentTitle: string
  messages: ChatMessage[]
  isProcessing: boolean
  onRemove: () => void
}

export function ChatPanel({ agentTitle, messages, isProcessing, onRemove }: ChatPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [messages, isProcessing])

  return <div className="flex flex-col h-full border rounded-lg bg-white overflow-hidden">
    <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
      <span className="font-medium text-sm truncate">{agentTitle}</span>
      <button onClick={onRemove} className="p-1 hover:bg-gray-200 rounded transition-colors">
        <X className="h-4 w-4 text-gray-500" />
      </button>
    </div>
    <div ref={scrollRef} className="grow overflow-y-auto p-3 space-y-3 text-sm">
      {messages.map((message, index) => {
        if (message.role === "user") {
          return <div key={index} className="flex justify-end">
            <div className="bg-blue-500 text-white rounded-lg px-3 py-0 max-w-[85%]">
              <ToMarkdown>{message.content}</ToMarkdown>
            </div>
          </div>
        }
        return <div key={index} className="flex gap-2">
          <Sparkles className="h-4 w-4 text-blue-500 mt-1 shrink-0" />
          <div className="bg-gray-100 rounded-lg px-3 py-2 max-w-[85%] border">
            <ToMarkdown>{message.content}</ToMarkdown>
          </div>
        </div>
      })}
      {isProcessing && <div className="flex gap-2 items-center">
        <Sparkles className="h-4 w-4 text-blue-500 shrink-0" />
        <div className="bg-gray-100 rounded-lg px-3 py-2 border">
          <LoaderCircle className="h-4 w-4 animate-spin text-gray-500" />
        </div>
      </div>}
    </div>
  </div>
}
